import React from "react";
import { decimalsFor } from "../utils/instrument";
import { useApi } from "../hooks/useApi";

const card = { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "10px", padding: "14px" };

function projColor(action) {
  if (action === "CLOSE")         return "#f87171";
  if (action === "PARTIAL_CLOSE") return "#fb923c";
  if (action === "TRAIL_SL")      return "#60a5fa";
  if (action === "ADJUST_SL")     return "#a78bfa";
  if (action === "TIGHTEN_SL")    return "#818cf8";
  if (action === "BREAKEVEN_LOCK") return "#34d399";
  if (action === "HOLD")          return "#64748b";
  return "#94a3b8";
}

function projIcon(action) {
  if (action === "CLOSE")         return "🔴";
  if (action === "PARTIAL_CLOSE") return "✂️";
  if (action === "TRAIL_SL")      return "🔒";
  if (action === "ADJUST_SL")     return "🛡️";
  if (action === "TIGHTEN_SL")    return "📐";
  if (action === "BREAKEVEN_LOCK") return "✅";
  if (action === "HOLD")          return "⏸";
  return "•";
}

export default function PositionProjection() {
  const { data: intel } = useApi("/intelligence", 15000);
  const { data: posData } = useApi("/positions", 5000);

  const positions  = posData?.positions || [];
  const symbols    = intel?.symbols    || [];

  // Only show symbols that have open positions
  const items = symbols
    .filter(s => s.projection)
    .map(s => ({
      ...s.projection,
      symbol: s.symbol,
      ai_confidence: s.ai_confidence,
    }));

  // Positions not matched by intelligence projection — group by symbol (3 tranches = 1 entry)
  const unmatchedRaw = positions.filter(p => !items.find(i => i.symbol === p.symbol));
  const unmatchedBySymbol = {};
  unmatchedRaw.forEach(p => {
    if (!unmatchedBySymbol[p.symbol]) {
      unmatchedBySymbol[p.symbol] = { ...p, profit: 0, lots: 0 };
    }
    unmatchedBySymbol[p.symbol].profit += p.profit || 0;
    unmatchedBySymbol[p.symbol].lots   += p.volume || 0;
  });
  const unmatched = Object.values(unmatchedBySymbol).map(p => ({
    symbol: p.symbol,
    type:   p.type,
    profit: Math.round(p.profit * 100) / 100,
    lots:   Math.round(p.lots * 100) / 100,
    pnl_pct: undefined,
    action: "HOLD",
    reason: "HOLD — Original SL working",
    sl: p.sl,
    tp: p.tp,
    in_recovery: false,
    recovery_phase: 0,
  }));

  const allItems = [...items, ...unmatched];

  return (
    <div style={card}>
      <div style={{ fontWeight: 600, color: "#94a3b8", marginBottom: "10px", fontSize: "13px" }}>
        🎯 Position Projections
        <span style={{ fontSize: "10px", color: "#334155", marginLeft: "8px" }}>Brain's next action</span>
      </div>

      {allItems.length === 0 && (
        <div style={{ color: "#334155", fontSize: "11px", padding: "10px 0" }}>No open positions</div>
      )}

      {allItems.map((p) => {
        // v5.16: per-instrument. The old list named GBPJPY/NZDJPY, retired long ago,
        // and still had no answer for an index or gold.
        const dp = decimalsFor(p.symbol, p.current_price ?? p.price_current ?? p.open_price);
        const pnlColor = (p.profit || 0) >= 0 ? "#4ade80" : "#f87171";

        return (
          <div key={p.symbol} style={{
            padding: "8px 10px", borderRadius: "8px", marginBottom: "6px",
            background: "#080c18",
            border: `1px solid ${projColor(p.action)}44`,
          }}>
            {/* Row 1: Symbol + direction + lots + P&L */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "12px" }}>{projIcon(p.action)}</span>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#e2e8f0" }}>{p.symbol}</span>
                <span style={{ fontSize: "10px", color: p.type === "BUY" ? "#4ade80" : "#f87171" }}>
                  {p.type} {p.lots ? `${p.lots}L` : ""}
                </span>
              </div>
              <span style={{ fontSize: "12px", fontWeight: 700, color: pnlColor }}>
                {(p.profit || 0) >= 0 ? "+" : ""}${(p.profit || 0).toFixed(2)}
                {p.pnl_pct !== undefined && (
                  <span style={{ fontSize: "9px", color: "#475569", marginLeft: "4px" }}>
                    ({p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct.toFixed(1)}%)
                  </span>
                )}
              </span>
            </div>

            {/* Row 2: Brain's next action + reason */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
              <span style={{
                fontSize: "11px", fontWeight: 600, color: projColor(p.action),
                background: projColor(p.action) + "22", padding: "1px 6px", borderRadius: "4px",
              }}>
                {p.action}
              </span>
              <span style={{ fontSize: "10px", color: "#64748b" }}>{p.reason}</span>
            </div>

            {/* Row 3: SL / TP / Recovery */}
            {(p.sl > 0 || p.tp > 0 || p.in_recovery) && (
              <div style={{ display: "flex", gap: "12px", fontSize: "10px" }}>
                {p.sl > 0 && <span style={{ color: "#f87171" }}>SL {p.sl?.toFixed(dp)}</span>}
                {p.tp > 0 && <span style={{ color: "#4ade80" }}>TP {p.tp?.toFixed(dp)}</span>}
                {p.in_recovery && (
                  <span style={{ color: "#fb923c" }}>Recovery P{p.recovery_phase}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
